import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';
import { Save, Upload, AlertCircle } from 'lucide-react';

interface Page {
  id: string;
  slug: string;
  title: string;
  content: any;
  meta_description: string;
}

export default function EditPages() {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState('/perplexity-logo.svg');

  useEffect(() => {
    fetchPages();
    fetchSettings();
  }, []);

  const fetchPages = async () => {
    try {
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setPages(data || []);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    const { data } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'logo_url')
      .single();

    if (data) {
      setLogoUrl(data.value);
    }
  };

  const handlePageUpdate = async (pageId: string, content: any) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const { error } = await supabase
        .from('pages')
        .update({ content })
        .eq('id', pageId);

      if (error) throw error;
      setSuccess('Page updated successfully!');
      fetchPages();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setSelectedFile(file);
      const fileExt = file.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;
      
      const { error: uploadError, data } = await supabase.storage
        .from('media')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(fileName);

      await supabase
        .from('settings')
        .update({ value: publicUrl })
        .eq('key', 'logo_url');

      setLogoUrl(publicUrl);
      setSuccess('Logo updated successfully!');
    } catch (error: any) {
      setError(error.message);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Edit Pages</h1>

        {(error || success) && (
          <div className={`p-4 rounded-md mb-6 ${error ? 'bg-red-500/10 border border-red-500' : 'bg-green-500/10 border border-green-500'}`}>
            <div className="flex items-center">
              <AlertCircle className={`h-5 w-5 ${error ? 'text-red-500' : 'text-green-500'} mr-2`} />
              <p className={error ? 'text-red-500' : 'text-green-500'}>
                {error || success}
              </p>
            </div>
          </div>
        )}

        <section className="bg-white rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Logo</h2>
          <div className="flex items-center space-x-4">
            <img src={logoUrl} alt="Current logo" className="h-12 w-auto" />
            <div>
              <label className="bg-gray-800 text-white px-4 py-2 rounded-lg inline-flex items-center cursor-pointer hover:bg-gray-700 transition-colors">
                <Upload className="h-5 w-5 mr-2" />
                Upload New Logo
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleLogoUpload}
                />
              </label>
              <p className="text-sm text-gray-600 mt-2">
                Recommended size: 200x50px, SVG or PNG format
              </p>
            </div>
          </div>
        </section>

        <section className="space-y-6">
          <div className="bg-white rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Terms of Service</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Content
                </label>
                <textarea
                  className="w-full h-64 bg-white text-gray-900 border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-teal-400"
                  defaultValue={pages.find(p => p.slug === 'terms')?.content?.text}
                  onChange={(e) => handlePageUpdate(
                    pages.find(p => p.slug === 'terms')?.id || '',
                    { text: e.target.value }
                  )}
                />
              </div>
              <button
                onClick={() => handlePageUpdate(
                  pages.find(p => p.slug === 'terms')?.id || '',
                  { text: pages.find(p => p.slug === 'terms')?.content?.text }
                )}
                disabled={saving}
                className="bg-gray-800 text-white px-4 py-2 rounded-lg inline-flex items-center hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                <Save className="h-5 w-5 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Privacy Statement</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Content
                </label>
                <textarea
                  className="w-full h-64 bg-white text-gray-900 border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-teal-400"
                  defaultValue={pages.find(p => p.slug === 'privacy')?.content?.text}
                  onChange={(e) => handlePageUpdate(
                    pages.find(p => p.slug === 'privacy')?.id || '',
                    { text: e.target.value }
                  )}
                />
              </div>
              <button
                onClick={() => handlePageUpdate(
                  pages.find(p => p.slug === 'privacy')?.id || '',
                  { text: pages.find(p => p.slug === 'privacy')?.content?.text }
                )}
                disabled={saving}
                className="bg-gray-800 text-white px-4 py-2 rounded-lg inline-flex items-center hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                <Save className="h-5 w-5 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}